import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  User as FirebaseUser 
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc 
} from 'firebase/firestore';
import { auth, db } from './config';
import { User } from '../types';

// Session expiration helpers
const SESSION_DURATION = 6 * 60 * 60 * 1000;
// 1 minute: 1 * 60 * 1000
// 12 hours: 12 * 60 * 60 * 1000
// 1 hour: 1 * 60 * 60 * 1000
// 7 days: 7 * 24 * 60 * 60 * 1000
const LOGIN_TIMESTAMP_KEY = 'loginTimestamp';

export const setLoginTimestamp = (): void => {
  localStorage.setItem(LOGIN_TIMESTAMP_KEY, Date.now().toString());
};

export const getLoginTimestamp = (): number | null => {
  const timestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
  return timestamp ? parseInt(timestamp, 10) : null;
};

export const clearLoginTimestamp = (): void => {
  localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
};

export const isSessionExpired = (): boolean => {
  const loginTimestamp = getLoginTimestamp();
  if (!loginTimestamp) return false; // No timestamp means fresh login, not expired
  
  const currentTime = Date.now();
  const timeDifference = currentTime - loginTimestamp;
  
  return timeDifference > SESSION_DURATION;
};

// Registration function
export const registerUser = async (
  email: string, 
  password: string, 
  username: string, 
  phoneNumber: string, 
  role: 'inputter' | 'authorizer' | 'operations'
): Promise<{ user: User; needsVerification: boolean }> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;
    
    // Send email verification
    await sendEmailVerification(firebaseUser);
    
    // Create user object
    const user: User = {
      id: firebaseUser.uid,
      email: firebaseUser.email || email,
      name: username,
      phoneNumber: phoneNumber,
      role: role
    };
    
    // Store user data in Firestore
    await setDoc(doc(db, 'users', firebaseUser.uid), {
      email: user.email,
      name: user.name,
      phoneNumber: user.phoneNumber,
      role: user.role,
      createdAt: new Date(),
      emailVerified: false,
      lastLogin: null
    });
    
    // Note: We don't sign out here to keep the authorizer signed in
    // The new user will need to verify their email before they can log in
    
    return { user, needsVerification: true };
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Login function
export const loginUser = async (email: string, password: string): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;
    
    // Check if email is verified
    if (!firebaseUser.emailVerified) {
      // Sign out the user since they're not verified
      await signOut(auth);
      throw new Error('Please verify your email address before logging in. Check your inbox for a verification email.');
    }
    
    // Get user data from Firestore
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    let user: User;
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      user = {
        id: firebaseUser.uid,
        email: firebaseUser.email || email,
        name: userData.name || email.split('@')[0],
        phoneNumber: userData.phoneNumber,
        role: userData.role
      };
      
      // Update last login and sync email verification status
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        lastLogin: new Date(),
        emailVerified: firebaseUser.emailVerified
      });
    } else {
      // Fallback for existing users without Firestore data
      user = {
        id: firebaseUser.uid,
        email: firebaseUser.email || email,
        name: email.split('@')[0],
        phoneNumber: '',
        role: undefined
      };
      
      // Store user data in Firestore
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        email: user.email,
        name: user.name,
        phoneNumber: user.phoneNumber,
        role: user.role,
        createdAt: new Date(),
        emailVerified: firebaseUser.emailVerified,
        lastLogin: new Date()
      });
    }
    
    // Set login timestamp for session expiration tracking
    setLoginTimestamp();
    
    return user;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Logout function
export const logoutUser = async (): Promise<void> => {
  try {
    // Clear login timestamp
    clearLoginTimestamp();
    await signOut(auth);
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Get current user data from Firestore
export const getCurrentUserData = async (uid: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        id: uid,
        email: userData.email,
        name: userData.name,
        phoneNumber: userData.phoneNumber,
        role: userData.role
      };
    }
    return null;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Update user's last login
export const updateLastLogin = async (uid: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', uid), {
      lastLogin: new Date()
    });
  } catch (error: any) {
    console.error('Error updating last login:', error);
  }
};

// Update email verification status in Firestore
export const updateEmailVerificationStatus = async (uid: string, emailVerified: boolean): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', uid), {
      emailVerified: emailVerified
    });
  } catch (error: any) {
    console.error('Error updating email verification status:', error);
  }
};

// Resend verification email
export const resendVerificationEmail = async (): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }
    
    if (user.emailVerified) {
      throw new Error('Email is already verified');
    }
    
    await sendEmailVerification(user);
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Send password reset email
export const sendPasswordReset = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Get all users from Firestore (for admin purposes)
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const { collection, getDocs, query, orderBy } = await import('firebase/firestore');
    const usersRef = collection(db, 'users');
    const q = query(usersRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const users: User[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      users.push({
        id: doc.id,
        email: data.email,
        name: data.name,
        phoneNumber: data.phoneNumber,
        role: data.role
      });
    });
    
    return users;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Delete user account (marks as deleted in Firestore)
export const deleteUserAccount = async (userId: string): Promise<void> => {
  try {
    const { deleteDoc } = await import('firebase/firestore');
    await deleteDoc(doc(db, 'users', userId));
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Auth state listener with automatic Firestore sync
export const onAuthStateChange = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    // If user is signed in, sync their email verification status with Firestore
    if (firebaseUser) {
      try {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Only update if the status has changed
          if (userData.emailVerified !== firebaseUser.emailVerified) {
            await updateEmailVerificationStatus(firebaseUser.uid, firebaseUser.emailVerified);
          }
        }
      } catch (error) {
        console.error('Error syncing email verification status:', error);
      }
    }
    
    // Call the original callback
    callback(firebaseUser);
  });
};
